import type { ComponentType } from "react";
import React, { useState } from "react";
import { connect } from "react-redux";
import {
    CompareButtonContainer,
    ConvertIntoIndustryBtnContainer,
    QueryBarButtonsContainer,
    QueryBarContainer,
    QueryBarIconButton,
    QueryBarInfoCardContainerStyles,
    StyledIconButton,
    SubNavOutsideOverlay,
} from "./styledComponents";
import { PlainTooltip } from "@similarweb/ui-components";
import { SegmentsQueryBarGroupItem } from "app/pages/segments/querybar/SegmentsQueryBarGroupItem";
import { SegmentsQueryBarSegmentItem } from "app/pages/segments/querybar/SegmentsQueryBarSegmentItem";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import type {
    ICustomSegment,
    ICustomSegmentAccount,
    ICustomSegmentsGroup,
} from "app/services/segments/segmentsApiService";
import { ESegmentShareState } from "app/services/segments/segmentsApiService";
import { MODE } from "app/pages/segments/analysis/SegmentsAnalysisTrafficContainerMode";
import { toggleCreateUpdateSegmentGroupModal } from "app/actions/segmentsModuleActions";
import { SegmentsUpsellButton } from "app/pages/segments/styleComponents";
import { itsMyOwnSegment } from "app/pages/segments/analysis/itsMyOwnSegment";
import { SegmentTooltip } from "app/components/React/Tooltip/SegmentTooltip/SegmentTooltip";
import { PopupTarget } from "./PopupTarget";
import { SegmentsQueryBarAutocomplete } from "app/pages/segments/querybar/SegmentsQueryBarAutocomplete";
import { translate } from "app/filters/i18nFilter";
import { SwTrack } from "app/services/SwTrack";
import { useCurrentStateAndParams } from "@uirouter/react";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import ConvertIntoIndustryBtn from "app/pages/segments/components/convert-into-industry-btn/ConvertIntoIndustryBtn";
import CustomIndustryModal from "app/pages/segments/components/custom-industry-modal/CustomIndustryModal";
import { MAX_SEGMENTS_ALLOWED_FOR_COMPARISON } from "../features/segments-comparison/constants";
import ViewRelatedIndustryBtn from "app/pages/segments/components/view-related-industry-btn/ViewRelatedIndustryBtn";
import {
    DISABLED_TOOLTIP,
    DISABLED_TOOLTIP_SHARED,
} from "app/pages/segments/components/convert-into-industry-btn/consts";
import { allTrackers } from "app/services/track/track";
import { PopupClickContainer } from "@similarweb/ui-components";
import SaveBtnPopup from "./components/save-btn-popup/SaveBtnPopup";
import { CustomCategoriesWizard } from "app/components/customCategoriesWizard/CustomCategoriesWizard";
import { EAssetType } from "app/UtilitiesAndConstants/types/assetType";
import type { INavigateToPlayers } from "app/pages/segments/components/custom-industry-modal/services/navigateToPlayers";
import { navigateToPlayers } from "app/pages/segments/components/custom-industry-modal/services/navigateToPlayers";
import CountryService from "app/services/CountryService";

interface ISegmentsQueryBarProps {
    params: { [key: string]: any };
    segments: ICustomSegment[];
    allSegments: ICustomSegmentAccount[];
    groups: ICustomSegmentsGroup[];
    toggleCreateUpdateSegmentGroupModal: Function;
    isMidTierUser?: boolean;
}

const SegmentsQueryBarComponent = (props: ISegmentsQueryBarProps) => {
    const { segments, allSegments, groups, toggleCreateUpdateSegmentGroupModal, isMidTierUser } = props;
    const { params } = useCurrentStateAndParams();
    const { mode, id } = params;

    const [showConvertIntoIndustryModal, setShowConvertIntoIndustryModal] = useState(false);
    const [showCustomCategoriesModal, setShowCustomCategoriesModal] = useState(false);
    const { segmentsGroup, singleSegment } = React.useMemo(() => {
        let segmentsGroup, singleSegment;
        switch (mode) {
            case MODE.group:
                segmentsGroup = SegmentsUtils.getSegmentGroupById(groups, id);
                break;
            case MODE.single:
                singleSegment = SegmentsUtils.getSegmentById({ segments: allSegments }, id) as ICustomSegmentAccount;
                break;
        }
        return { segmentsGroup, singleSegment };
    }, [mode, id, groups, allSegments, toggleCreateUpdateSegmentGroupModal]);

    const [mySegmentsSorted, accountSegmentsSorted, myGroupsSorted] = React.useMemo(() => {
        const allowedSegmentFilter = (seg) => !seg.isLocked;
        return [
            (segments ?? [])
                .filter(allowedSegmentFilter)
                .filter((segment) => segment.id !== singleSegment?.id)
                .sort(SegmentsUtils.sortSegments.byDomainName),
            (allSegments ?? [])
                .filter(allowedSegmentFilter)
                .filter((segment) => segment.id !== singleSegment?.id && !itsMyOwnSegment(segment.userId))
                .sort(SegmentsUtils.sortSegments.byDomainName),
            (groups ?? [])
                .filter((group) => group.id !== segmentsGroup?.id)
                .sort(SegmentsUtils.sortSegmentsGroups.byName),
        ];
    }, [segments, allSegments, groups, segmentsGroup?.id, singleSegment?.id]);

    const [isOpenMainQueryBarPopup, setIsOpenMainQueryBarPopup] = React.useState(false);
    const opMainQueryBarPopup = React.useMemo(
        () => ({
            toggle: () => setIsOpenMainQueryBarPopup((prevIsOpen) => !prevIsOpen),
            open: () => setIsOpenMainQueryBarPopup(true),
            close: () => setIsOpenMainQueryBarPopup(false),
        }),
        [setIsOpenMainQueryBarPopup],
    );

    const onConvertIntoIndustryBtnClick = () => {
        allTrackers.dispatchTrackEvent("Button", "click", "View in market analysis/create industry");
        setShowConvertIntoIndustryModal(true);
    };

    const editSegmentClick = React.useCallback(() => {
        SwTrack.all.trackEvent("Internal Link", "click", `segmentCard/editSegment/${singleSegment.id}`);
        const currentModule = proNavigator.getCurrentModule();
        const segmentWizardState = `${currentModule}-wizard`;
        proNavigator.go(segmentWizardState, { sid: singleSegment.id });
    }, [singleSegment?.id]);

    const onClickCompare = () => {
        allTrackers.dispatchTrackEvent("Button", "click", "Segments/Single/Add");
    };

    const onNewCustomIndustryClick = () => {
        setShowCustomCategoriesModal(true);
    };

    const onNewSegmentComparisonClick = () => {
        toggleCreateUpdateSegmentGroupModal(true, undefined, singleSegment.id);
    };

    const onClickEditGroup = React.useCallback(() => {
        toggleCreateUpdateSegmentGroupModal(true, segmentsGroup);
        SwTrack.all.trackEvent("Button", "click", `Segments/Group/Edit/${segmentsGroup.id}`);
    }, [toggleCreateUpdateSegmentGroupModal, segmentsGroup]);

    const navigateTrackEventSingle = (entity: ICustomSegment) => {
        const { domain, segmentName, id } = entity;
        allTrackers.dispatchTrackEvent("Query Bar", "Navigate", "Segments/single", {
            Segment_Domain: domain,
            Segment_Name: segmentName,
            Segment_ID: id,
            Segment_Type: "custom",
        });
    };

    const navigateTrackEventGroup = (entity: ICustomSegmentsGroup) => {
        allTrackers.dispatchTrackEvent("Query Bar", "Navigate", "Segments/group", {
            SegmentComparison_ID: entity.id,
        });
    };

    const navigateChangeEntity = (mode: string, entity: ICustomSegment | ICustomSegmentsGroup, skipTrackEvent) => {
        proNavigator.updateParams({ mode, id: entity.id });
        if (!skipTrackEvent) {
            if (mode === MODE.single) {
                navigateTrackEventSingle(entity as ICustomSegment);
            } else {
                navigateTrackEventGroup(entity as ICustomSegmentsGroup);
            }
        }
    };

    const sharedWithMeSegments = allSegments.filter(
        (segment) =>
            segment.targetType === ESegmentShareState.RESTRICTED &&
            !segments.some((mySegment) => mySegment.id === segment.id),
    );

    const onViewRelatedIndustryBtnClick = () => {
        allTrackers.dispatchTrackEvent("Button", "click", "View in market analysis/access industry");
    };

    const renderQueryBarMainPopup = () => {
        return (
            <SegmentsQueryBarAutocomplete
                mySegments={mySegmentsSorted}
                accountSegments={accountSegmentsSorted}
                sharedWithMeSegments={sharedWithMeSegments}
                myGroups={myGroupsSorted}
                navigateChangeEntity={navigateChangeEntity}
                defaultTab={{ single: "segments", group: "groups" }[mode]}
                closeFn={opMainQueryBarPopup.close}
            />
        );
    };

    const renderQueryBarMainItem = () => {
        switch (params.mode) {
            case "single":
                return (
                    singleSegment && (
                        <>
                            <QueryBarInfoCardContainerStyles />
                            <PopupTarget
                                isOpen={isOpenMainQueryBarPopup}
                                renderPopup={renderQueryBarMainPopup}
                                closeFn={opMainQueryBarPopup.close}
                            >
                                <SegmentTooltip
                                    dateModified={singleSegment.lastUpdated}
                                    segmentName={singleSegment.segmentName}
                                    domain={singleSegment.domain}
                                    isOrgSegment={!itsMyOwnSegment(singleSegment.userId)}
                                    segmentId={singleSegment.id}
                                    onClick={editSegmentClick}
                                    debounce={600}
                                    cssClassContainer="QueryBarInfoCardContainer"
                                >
                                    <div>
                                        <SegmentsQueryBarSegmentItem
                                            segmentName={singleSegment.segmentName}
                                            segmentWebsite={singleSegment.domain}
                                            segmentImage={singleSegment.favicon}
                                            onItemClick={opMainQueryBarPopup.toggle}
                                        />
                                    </div>
                                </SegmentTooltip>
                            </PopupTarget>
                        </>
                    )
                );
            case "group":
                return (
                    segmentsGroup && (
                        <PopupTarget
                            isOpen={isOpenMainQueryBarPopup}
                            renderPopup={renderQueryBarMainPopup}
                            closeFn={opMainQueryBarPopup.close}
                        >
                            <SegmentsQueryBarGroupItem
                                groupName={segmentsGroup.name}
                                groupSize={segmentsGroup.members.length}
                                onItemClick={opMainQueryBarPopup.toggle}
                            />
                        </PopupTarget>
                    )
                );
        }
    };

    const onCustomCategoriesWizardSave = async (modal, data) => {
        const { categoryName, country } = modal.ctrl;
        const navigateParams: INavigateToPlayers = { marketName: categoryName };
        if (country?.id) {
            navigateParams.country = country.id;
        }
        navigateToPlayers(navigateParams);
        setShowCustomCategoriesModal(false);
    };

    const getInitialSelectedCountry = () => {
        const { country } = proNavigator.getParams();
        if (country) {
            return CountryService.getCountryById(country);
        }
    };

    const renderIndustryModal = () => {
        if (!singleSegment) {
            return null;
        }
        const { segmentName, domain, id, favicon } = singleSegment;
        return (
            <CustomCategoriesWizard
                isOpen={showCustomCategoriesModal}
                onClose={() => {
                    setShowCustomCategoriesModal(false);
                }}
                wizardProps={{
                    onSave: onCustomCategoriesWizardSave,
                    stayOnPage: true,
                    initialSelectedCountry: getInitialSelectedCountry(),
                    importedEntities: [
                        {
                            name: domain,
                            favicon: favicon,
                            subName: segmentName,
                            id: id,
                            valid: true,
                            type: EAssetType.Segment,
                        },
                    ],
                }}
            />
        );
    };

    const renderButtons = () => {
        let isSharedWithMe;

        switch (mode) {
            case MODE.single:
                isSharedWithMe = !segments.some((segment) => segment.id === id);
                return isMidTierUser ? (
                    <SegmentsUpsellButton onClick={SegmentsUtils.openMidTierUserUpsellModal}>
                        {translate("segments.module.header.compare.btn")}
                    </SegmentsUpsellButton>
                ) : (
                    <>
                        <QueryBarButtonsContainer>
                            <CompareButtonContainer>
                                <PopupClickContainer
                                    content={() => (
                                        <SaveBtnPopup
                                            onNewCustomIndustryClick={onNewCustomIndustryClick}
                                            onNewSegmentComparisonClick={onNewSegmentComparisonClick}
                                            isNewIndustryDisabled={isSharedWithMe}
                                        />
                                    )}
                                    config={{
                                        placement: "top",
                                        enabled: true,
                                        width: 318,
                                        cssClassContent: "compare-button-popup-wrapper",
                                    }}
                                    appendTo={".compare-button"}
                                >
                                    <div>
                                        <StyledIconButton
                                            onClick={onClickCompare}
                                            iconName="arrow"
                                            iconSize="10px"
                                            className={"compare-button"}
                                            placement={"right"}
                                        >
                                            {translate("segment.analysis.query.bar.single.save.button")}
                                        </StyledIconButton>
                                    </div>
                                </PopupClickContainer>
                            </CompareButtonContainer>
                            {/* we will hide the add to group dropdown for a while as a product requerment */}
                            {/* <AddToGroupDropdown groups={groups} currentSegmentId={id} /> */}
                        </QueryBarButtonsContainer>
                        {renderIndustryModal()}
                    </>
                );
            case MODE.group:
                isSharedWithMe = segmentsGroup.segments.some(
                    (segmentId) => !segments.some((segment) => segment.id === segmentId),
                );

                const shouldHideConvertBtn =
                    isSharedWithMe || segmentsGroup.members.length > MAX_SEGMENTS_ALLOWED_FOR_COMPARISON;

                const disabledTooltip = isSharedWithMe ? DISABLED_TOOLTIP_SHARED : DISABLED_TOOLTIP;

                return (
                    <>
                        <QueryBarButtonsContainer>
                            <PlainTooltip
                                placement="bottom"
                                tooltipContent={translate("segments.querybar.group.edit.tooltip")}
                            >
                                <span>
                                    <QueryBarIconButton
                                        onClick={onClickEditGroup}
                                        iconName="edit-group"
                                        type="flat"
                                        iconSize="sm"
                                    />
                                </span>
                            </PlainTooltip>
                            {segmentsGroup.customIndustryId ? (
                                <ConvertIntoIndustryBtnContainer>
                                    <ViewRelatedIndustryBtn
                                        customIndustryId={segmentsGroup.customIndustryId}
                                        trackingEvent={onViewRelatedIndustryBtnClick}
                                    />
                                </ConvertIntoIndustryBtnContainer>
                            ) : (
                                !shouldHideConvertBtn && (
                                    <ConvertIntoIndustryBtnContainer>
                                        <ConvertIntoIndustryBtn
                                            segmentGroupProps={{
                                                id: segmentsGroup.id,
                                                name: segmentsGroup.name,
                                            }}
                                            onClick={onConvertIntoIndustryBtnClick}
                                            disabledTooltip={disabledTooltip}
                                        />
                                    </ConvertIntoIndustryBtnContainer>
                                )
                            )}
                        </QueryBarButtonsContainer>
                        <CustomIndustryModal
                            showModal={showConvertIntoIndustryModal}
                            setShowModal={setShowConvertIntoIndustryModal}
                            segmentGroupName={segmentsGroup.name}
                            segmentGroupId={segmentsGroup.id}
                        />
                    </>
                );
        }
    };

    return (
        <QueryBarContainer>
            {renderQueryBarMainItem()}
            {renderButtons()}
            {isOpenMainQueryBarPopup ? <SubNavOutsideOverlay /> : null}
        </QueryBarContainer>
    );
};

const mapStateToProps = (store) => {
    const {
        segmentsModule: { customSegmentsMeta },
    } = store;
    return {
        segments: customSegmentsMeta?.Segments,
        allSegments: customSegmentsMeta?.AccountSegments,
        groups: customSegmentsMeta?.SegmentGroups,
    };
};

const mapDispatchToProps = {
    toggleCreateUpdateSegmentGroupModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(SegmentsQueryBarComponent as ComponentType<any>);
